import { useCallback } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import useUserSetting, { useMyUserSettingUpdate } from '@shared/hooks/src/useUserSetting'
import { UserSettingName } from '@shared/utils'

export const COLOR_SCHEME_KEY = 'color-scheme'
export const DEFAULT_COLOR_SCHEME = 'light'

/**
 * Color scheme (light/dark) backed by the backend user setting.
 *
 * The backend is the source of truth, but the value is also mirrored to
 * localStorage because `ThemeProvider` is mounted above `MeProvider` and needs a
 * synchronous value to paint the initial theme before `me` is loaded.
 *
 * @param {Object} me - The current user.
 * @returns {[string, (mode: string) => void, boolean]} The current mode, a
 *   setter that persists to the backend and updates the mirror, and the pending
 *   state of the backend update.
 */
export default function useColorScheme(me) {
  const setting = useUserSetting(me, UserSettingName.ColorScheme)
  const updateUserSetting = useMyUserSettingUpdate(me)
  const [localMode, setLocalMode] = useLocalStorage(COLOR_SCHEME_KEY, DEFAULT_COLOR_SCHEME)

  const settingId = setting?.id
  const mode = setting?.value ?? localMode

  const setMode = useCallback(
    (newMode) => {
      // Update the localStorage mirror first so ThemeProvider repaints instantly
      setLocalMode(newMode)
      if (settingId) updateUserSetting.mutateAsync({ settingId, value: newMode })
    },
    [setLocalMode, settingId, updateUserSetting]
  )

  return [mode, setMode, updateUserSetting.isPending]
}
